import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ScreeningItem, ScreeningPscInspection } from "../types/Screening";
import { AppThunk, RootState } from "../redux/store";
import { getMainShipImage, getScreeningData, getScreeningPscData } from "../services/ScreeningService";
import { createParametricSelectorHook } from "../redux/hooks";
import { userSliceSelector } from "./userSlice";

export interface ScreeningState {
    data: ScreeningItem[];
    mainShipImage: string;
    loading: boolean;
    error: boolean;
    pscLoading: boolean;
    pscError: boolean;
    pscInspections: ScreeningPscInspection[];
}

export const initialState: ScreeningState = {
    data: [],
    mainShipImage: "",
    loading: true,
    error: false,
    pscLoading: true,
    pscError: false,
    pscInspections: [],
};

export const screeningSlice = createSlice({
    name: "screening",
    initialState,
    reducers: {
        addData: (state, action: PayloadAction<ScreeningItem>) => {
            state.data = state.data.concat([action.payload]);
        },
        setImage: (state, action: PayloadAction<string>) => {
            state.mainShipImage = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<boolean>) => {
            state.error = action.payload;
        },
        setPscLoading: (state, action: PayloadAction<boolean>) => {
            state.pscLoading = action.payload;
        },
        setPscError: (state, action: PayloadAction<boolean>) => {
            state.pscError = action.payload;
        },
        setPscInspections: (state, action: PayloadAction<ScreeningPscInspection[]>) => {
            state.pscInspections = action.payload;
        },
    }
});

export const {
    addData,
    setImage,
    setLoading,
    setError,
    setPscLoading,
    setPscError,
    setPscInspections
} = screeningSlice.actions;

export default screeningSlice.reducer;

export const fetchScreeningData =
    (imo: string): AppThunk =>
    async (dispatch) => {
        dispatch(setLoading(true));
        
        try {
            const response = await getScreeningData([imo]);

            if (response?.length) {
                dispatch(addData(response[0]));
            } else {
                throw new Error("no data");
            }
        } catch (e) {
            console.error(e);
            dispatch(setError(true));
        }

        dispatch(setLoading(false));
    };

export const fetchMainShipImage =
(imo: string): AppThunk =>
async (dispatch) => {
    try {
        const response = await getMainShipImage(imo);

        if (response?.length) {
            dispatch(setImage(response));
        } else {
            throw new Error("no image");
        }
    } catch (e) {
        console.error(e);
        dispatch(setError(true));
    }

    dispatch(setLoading(false));
};

export const fetchScreeningPscData =
    (imo: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setPscLoading(true));

        const { UserToken } = userSliceSelector(getState());

        try {
            const response = await getScreeningPscData(imo, UserToken);

            if (response) {
                dispatch(setPscInspections(response.inspections));
                // set others here like defiencies
            } else {
                throw new Error("no data");
            }
        } catch (e) {
            console.error(e);
            dispatch(setPscError(true));
        }

        dispatch(setPscLoading(false));
    };

export const selectScreening = (state: RootState) =>
    state.Screening;

export const selectScreeningLoading = (state: RootState) =>
    state.Screening.loading;

export const selectScreeningByIMO = createParametricSelectorHook(
    createSelector(
        [
            (state: RootState) => state.Screening.data,
            (_, imo: string | undefined) => imo
        ],
        (screeningData, imo) => {
            if (imo) {
                return screeningData.find(
                    (screening) => screening.vessel_imo === Number(imo)
                );
            }
        }
    )
);

export const selectMainShipImage = (state: RootState) => state.Screening.mainShipImage;
